import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import reduxForm from 'redux-form/es/reduxForm';
import map from 'lodash/map';
import { fieldNames, formName, questionsId } from './const';

import Spinner from '../../Core/Spinner';
import AlternativeIncomeQuestion from './questions/AlternativeIncomeQuestion';
import CentreLinkQuestion from './questions/CentreLinkQuestion';
import UnsureLenderQuestion from './questions/UnsureLenderQuestion';
import EssentialNeedsQuestion from './questions/EssentialNeedsQuestion';
import OngoingTransferQuestion from './questions/OngoingTransferQuestion';
import HousingExpensesQuestion from './questions/HousingExpensesQuestion';
import OtherExpensesQuestion from './questions/OtherExpensesQuestion';
import RecentCreditEnquiryQuestion from './questions/RecentCreditEnquiryQuestion';
import Button from '../../../common/Button';
import followUpValidation from './helpers/followUpValidation';

const FollowUp = (props) => {
  const {
    followUpQuestions,
    followUpQuestionsLoaded,
    form,
    handleSubmit,
    onSubmit,
    submitting,
  } = props;

  if (followUpQuestions.length === 0 && !followUpQuestionsLoaded) {
    return <Spinner />;
  }

  const centreLinkAnswerValue = form && form.values && form.values[fieldNames.governmentBenefits];
  const essentialNeedsAnswerValue = form && form.values && form.values[fieldNames.essentialNeeds];
  // eslint-disable-next-line max-len
  const housingExpensesValue = form && form.values && form.values[fieldNames.housingExpenses];

  const renderOngoingTransferQuestion = (bankStatements) => map(bankStatements, (statement, id) => {
    const shouldShowExtraPartOfTransferQuestion = form && form.values && form.values[`${fieldNames.ongoingTransfers}-${id}`] === 'YES';
    const ongoingTransferExtraPartValue = form && form.values && form.values[`${fieldNames.ongoingTransfersExtraPart}-${id}`];

    return (
      <OngoingTransferQuestion
        key={id}
        id={id}
        bankStatementOngoingTransfersMonthlyAmount={statement}
        shouldShowExtraPartOfTransferQuestion={shouldShowExtraPartOfTransferQuestion}
        selectedValue={ongoingTransferExtraPartValue}
      />
    );
  });

  const renderCreditEnquiryQuestion = (questionId, lendersApplications = []) => {
    const selectedValues = lendersApplications.map(({ lenderName }) => {
      const lenderId = lenderName.replaceAll(/[^A-Z0-9]+/ig, '');
      const value = form && form.values && form.values[`${fieldNames.creditEnquiry}-${lenderId}`];
      return { lenderId, answer: value };
    });
    return (
      <RecentCreditEnquiryQuestion
        key={questionId}
        otherLendersApplications={lendersApplications}
        selectedValues={selectedValues}
      />
    );
  };

  return (
    <Fragment>
      <h1 className="followup__title">
        <FormattedMessage id="followup.header" />
      </h1>

      <div className="content-block">
        <h3 className="followup__subheader">
          <FormattedMessage id="followup.subheader" />
        </h3>

        <form onSubmit={handleSubmit(onSubmit)}>
          {
            map(followUpQuestions, (question) => {
              switch (question.questionId) {
                case questionsId.alternativeIncome:
                  return (
                    <AlternativeIncomeQuestion
                      key={question.questionId}
                      bankStatementSalary={question.bankStatementSalary}
                      declaredSalary={question.declaredSalary}
                      declaredSalaryMonthly={question.declaredSalaryMonthly}
                    />
                  );
                case questionsId.undeclaredCentrelink:
                  return (
                    <CentreLinkQuestion
                      key={question.questionId}
                      bankStatementGovernmentBenefits={question.bankStatementGovernmentBenefits}
                      selectedValue={centreLinkAnswerValue}
                    />
                  );
                case questionsId.unsureLenderRepayment:
                  return (
                    <UnsureLenderQuestion
                      key={question.questionId}
                      otherLendersFunds={question.otherLendersFunds}
                    />
                  );
                case questionsId.essentialNeedsAsLoanPurpose:
                  return (
                    <EssentialNeedsQuestion
                      key={question.questionId}
                      essentialNeedsPurposes={question.essentialNeedsPurposes}
                      selectedValue={essentialNeedsAnswerValue}
                    />
                  );
                case questionsId.ongoingTransfers:
                  // eslint-disable-next-line max-len
                  return renderOngoingTransferQuestion(question.bankStatementOngoingMonthlyTransfers);
                case questionsId.housingExpensesShare:
                  return (
                    <HousingExpensesQuestion
                      key={question.questionId}
                      declaredHousingCosts={question.declaredHousingCosts}
                      selectedValue={housingExpensesValue}
                    />
                  );
                case questionsId.otherExpenses:
                  return <OtherExpensesQuestion key={question.questionId} />;
                case questionsId.recentCreditEnquiry:
                  // eslint-disable-next-line max-len
                  return renderCreditEnquiryQuestion(question.questionId, question.otherLendersApplications);
                default:
                  return null;
              }
            })
          }

          <div className="followup__footer">
            <Button
              type="submit"
              isLoading={submitting}
            >
              <FormattedMessage id="followup.question.submit" />
            </Button>
          </div>
        </form>
      </div>

    </Fragment>
  );
};

FollowUp.propTypes = {
  followUpQuestions: PropTypes.array,
  form: PropTypes.object,
  handleSubmit: PropTypes.func,
  onSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  followUpQuestionsLoaded: PropTypes.bool,
};

const formConfig = {
  form: formName,
  shouldValidate: () => true,
  validate: (values, form) => followUpValidation(values, form),
  onSubmitFail: (error) => {
    if (DEBUG) {
      console.warn('form submit fail', error);
    }

    setTimeout(() => {
      let firstError = document.querySelector("[class*='error']");
      if (firstError) {
        firstError = firstError.parentElement;
        firstError.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      return null;
    }, 100);
  },
};

export default reduxForm(formConfig)(connect((state) => ({
  form: state.form[formName],
}), null)(FollowUp));
